import { Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "~/common/mui";
import classNames from "classnames";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { ProductService } from "~/features/addToCart/api/ProductService";
import type { OptionalPartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import { type AddToCartPartItem, type AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { LinkButton } from "~/common/components/Link";
import Spinner from "~/common/components/Spinner/Spinner";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import type { RootState } from "~/app/store";
import { formatCurrency } from "~/common/utils/currencyFormatter";
import { userIsCustomer } from "~/domains/user/utils/roles";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import CenterContents from "~/common/components/CenterContents";
import { volatileSum, zero } from "~/utils/PriceUtils";
import { Translation } from "~/app/i18n/Translation";
import { IMG_MISSING_PRODUCT } from "~/utils/url/staticResources";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { Alert } from "~/common/mui/Alert/Alert";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import ConnectedCPDProgress, {
  getCPDProgressData,
} from "~/features/cms/homepagePersonalization/cpd/components/cpdProgress/ConnectedCPDProgress";
import classes from "../CartSuccessModal.module.less";
import {
  type AddToCartResultFormData,
  getMultipleEntriesRows,
  getQuantity,
  isExtendedSubmitData,
  type ExtendedAddToCartFormData,
  type MultipleEntriesRow,
  shouldRenderMultipleEntriesResult,
} from "./addToCartResultUtils";

interface AddToCartResultProps {
  formData: AddToCartResultFormData;
  productInfo?: ProductData;
  isTablet: boolean;
  isLinear?: boolean;
  responseItems?: AddToCartResponseItem[];
}

const SummaryLabel = ({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) => {
  return (
    <div className={classNames("d-flex flex-justify-between", className)}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
};

const SingleEntry = ({
  formData,
  productInfo,
  isTablet,
  isLinear,
}: {
  formData: AddToCartResultFormData;
  productInfo?: ProductData;
  isTablet: boolean;
  isLinear?: boolean;
}) => {
  const { t } = useTranslation();
  const isBusiness = userIsCustomer();

  const firstItem = formData[0];
  const priceReference = isExtendedSubmitData(firstItem)
    ? firstItem.formValues.priceReference
    : firstItem.priceReference ?? "";

  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productInfo?.code ?? "",
    priceReference,
  });

  if (!productInfo) {
    return (
      <CenterContents>
        <CircleSpinner />
      </CenterContents>
    );
  }

  const isHousingKit = isKITProduct(productInfo);

  let netPrice: number | null;
  let basePrice: number | null = null;

  let hasSales = false;

  const extendedCartItem = firstItem as ExtendedAddToCartFormData;
  const tariffSurcharge = isTariffSurchargeEnabled() && (extendedCartItem.derived?.netPrice?.tariffSurcharge ?? false);

  const salesData = calculateSalesCampaign4PDP({ productDescriptor });
  const selectedPackCode = extendedCartItem.derived?.selectedPack?.packCode ?? INVALID_PACKAGE_CODE;

  hasSales = !!salesData.data?.salePerPack[selectedPackCode];

  if (extendedCartItem.saleNetPrice) {
    hasSales = true;
    netPrice = extendedCartItem.saleNetPrice;
    if (extendedCartItem.derived?.netPrice) {
      basePrice = extendedCartItem.derived.netPrice.value;
    }
  } else if (extendedCartItem.derived?.netPrice) {
    netPrice = extendedCartItem.derived.netPrice.value;
  } else {
    netPrice = null;
  }

  if (netPrice !== null && isHousingKit) {
    let optionalPartsPrice = 0;
    if (isExtendedSubmitData(firstItem)) {
      const optParts = Object.values(firstItem.optionalParts) as OptionalPartSubmitData[];
      optionalPartsPrice = volatileSum(
        ...optParts.filter((part) => part.formValues.selected).map((part) => part.derived.netPrice ?? zero()),
      ).value;
    } else if (Array.isArray(firstItem.optionalParts)) {
      const optionalParts = firstItem.optionalParts as AddToCartPartItem[];
      optionalPartsPrice = volatileSum(
        ...optionalParts.map((part) => ({ value: part.netPrice, currencyIso: "" })),
      ).value;
    }

    netPrice = optionalPartsPrice ? netPrice + optionalPartsPrice : netPrice;
  }

  const imageUrl = isLinear
    ? productInfo.images.find((image) => image.format === ImageFormat.thumbnail)?.url
    : productInfo.primaryImage?.url;

  const isCPDProgressEnabled = window.SCHA.feature.cpdBannerDrawer && !!getCPDProgressData();

  return (
    <Stack gap={2}>
      {isCPDProgressEnabled ? (
        <Stack gap={2}>
          <Typography variant="titleLarge">{t("addToCart.successMessage")}</Typography>
          <ConnectedCPDProgress />
          <Divider />
        </Stack>
      ) : (
        <Alert severity="success">{t("addToCart.successMessage")}</Alert>
      )}
      <div className={classes.content}>
        {!isTablet && <div className={classes.spacer} />}
        <div className={classes.imageContainer}>
          <img src={imageUrl ?? IMG_MISSING_PRODUCT} />
          {hasSales && <SaleTag className={classes.salesTag} size={24} />}
        </div>

        <div className={classes.productInfo}>
          <Stack gap={1} direction="row">
            {productInfo.preferred && <Icon className={classes.icon} type={IconType.Star} />}
            <p className={classes.designation}>{productInfo.designation}</p>
          </Stack>
          {isLinear ? (
            <LanguageAwareExternalContent content={productInfo.shortDescription.value} />
          ) : (
            <Translation {...productInfo.shortDescription} />
          )}
        </div>
        <div className={classes.productValues}>
          <SummaryLabel label={t("feedback.option.quantity")} value={getQuantity(firstItem).toString()} />
          {isBusiness && (
            <>
              <SummaryLabel
                label={t("addToCart.netPricePerPiece")}
                value={
                  netPrice ? (
                    basePrice ? (
                      <div className={classes.salesNetPriceContainer}>
                        <span className={classes.basePrice}>{formatCurrency(basePrice)}</span>
                        <span className={classes.newPrice}>{formatCurrency(netPrice)}</span>
                      </div>
                    ) : (
                      <span className={classNames(hasSales && classes.newPrice)}>{formatCurrency(netPrice)}</span>
                    )
                  ) : (
                    "-"
                  )
                }
              />
              <SummaryLabel
                className="f-bold"
                label={t("wishlistPage.total")}
                value={
                  netPrice ? `${formatCurrency(netPrice * getQuantity(firstItem))}${tariffSurcharge ? "*" : ""}` : "-"
                }
              />
              {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
            </>
          )}
        </div>
      </div>
    </Stack>
  );
};

const ProductsTable = ({ data }: { data: MultipleEntriesRow[] }) => {
  const { t } = useTranslation();

  const getProductsBasicInfo = (state: RootState) => state.products.basic;
  const productsBasicInfo = useSelector(getProductsBasicInfo);
  if (!data) {
    return <Spinner />;
  }
  const entries = data.map((productData, index) => {
    const designation =
      productData.designation ?? productsBasicInfo[productData.productCode]?.designation ?? productData.productCode;

    return (
      <TableRow key={`${productData.productCode}-${index}`}>
        <TableCell>
          <div className={classes.cell}>
            <p className="f-bold m-0 mr-1">{designation}</p>
            {productData.saleNetPrice && <SaleTag size={16} />}
          </div>
        </TableCell>
        <TableCell>
          <p className="m-0">{productData.quantity}</p>
        </TableCell>
      </TableRow>
    );
  });
  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell>{t("feedback.option.invoiceProduct")}</TableCell>
          <TableCell className={classes.headerLabel}>{t("input.quantity")}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>{entries}</TableBody>
    </Table>
  );
};

const MultipleEntries = ({ entries }: { entries: MultipleEntriesRow[] }) => {
  const { t } = useTranslation();
  const { val: table, setTrue: open, setFalse: close } = useBooleanState(true);
  const totalPieces = entries.reduce((accumulator, currentValue) => accumulator + currentValue.quantity, 0);

  return (
    <Stack gap={2}>
      <Alert severity="success">
        {t("addToCart.multipleProductsAdded", { count: entries.length, pieces: totalPieces })}
      </Alert>
      <div className={classNames(classes.content, classes.multiple)}>
        <LinkButton
          iconBefore={table ? <Icon type={IconType.ChevronUp} /> : <Icon type={IconType.ChevronDown} />}
          className={classes.toggle}
          onClick={() => (table ? close() : open())}
        >
          {table ? t("addToCart.hideTable") : t("addToCart.showTable")}
        </LinkButton>
        {table && <ProductsTable data={entries} />}
      </div>
    </Stack>
  );
};

const AddToCartResult = ({ formData, productInfo, isTablet, isLinear, responseItems }: AddToCartResultProps) => {
  const phaseOut2Enabled = isPhaseOut2Enabled();
  const hasMultiple = shouldRenderMultipleEntriesResult({ formData, productInfo, responseItems, phaseOut2Enabled });

  if (hasMultiple) {
    return (
      <MultipleEntries
        entries={getMultipleEntriesRows({
          formData,
          productInfo,
          responseItems,
          phaseOut2Enabled,
        })}
      />
    );
  }

  return <SingleEntry productInfo={productInfo} formData={formData} isTablet={isTablet} isLinear={isLinear} />;
};

export default AddToCartResult;
